import { supabase } from '../lib/supabase';

export const interviewService = {
  /**
   * Schedule a new interview
   */
  async scheduleInterview(interviewData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate required fields
      if (!interviewData?.applicationId || !interviewData?.candidateId || !interviewData?.jobId) {
        throw new Error('Missing required fields');
      }

      // Convert camelCase to snake_case for database
      const dbData = {
        application_id: interviewData?.applicationId,
        employer_id: user?.id,
        candidate_id: interviewData?.candidateId,
        job_id: interviewData?.jobId,
        interview_date: interviewData?.interviewDate,
        interview_time: interviewData?.interviewTime,
        duration_minutes: interviewData?.durationMinutes || 60,
        location: interviewData?.location || null,
        meeting_link: interviewData?.meetingLink || null,
        interview_type: interviewData?.interviewType,
        interviewer_name: interviewData?.interviewerName,
        interviewer_email: interviewData?.interviewerEmail,
        notes: interviewData?.notes || null,
        status: 'scheduled'
      };

      const { data, error } = await supabase?.from('interview_schedules')?.insert(dbData)?.select()?.single();

      if (error) throw error;

      // Send email notification to candidate
      await this.sendInterviewNotification({
        candidateEmail: interviewData?.candidateEmail,
        candidateName: interviewData?.candidateName,
        interviewDate: interviewData?.interviewDate,
        interviewTime: interviewData?.interviewTime,
        location: interviewData?.location,
        meetingLink: interviewData?.meetingLink,
        interviewType: interviewData?.interviewType,
        interviewerName: interviewData?.interviewerName,
        jobTitle: interviewData?.jobTitle,
        companyName: interviewData?.companyName,
        duration: interviewData?.durationMinutes
      });

      // Convert snake_case to camelCase for React
      return {
        id: data?.id,
        applicationId: data?.application_id,
        employerId: data?.employer_id,
        candidateId: data?.candidate_id,
        jobId: data?.job_id,
        interviewDate: data?.interview_date,
        interviewTime: data?.interview_time,
        durationMinutes: data?.duration_minutes,
        location: data?.location,
        meetingLink: data?.meeting_link,
        interviewType: data?.interview_type,
        interviewerName: data?.interviewer_name,
        interviewerEmail: data?.interviewer_email,
        notes: data?.notes,
        status: data?.status,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error scheduling interview:', error);
      throw error;
    }
  },

  /**
   * Get interviews for a specific application
   */
  async getInterviewsByApplication(applicationId) {
    try {
      const { data, error } = await supabase?.from('interview_schedules')?.select('*')?.eq('application_id', applicationId)?.order('interview_date', { ascending: true });

      if (error) throw error;

      return data?.map(interview => ({
        id: interview?.id,
        applicationId: interview?.application_id,
        employerId: interview?.employer_id,
        candidateId: interview?.candidate_id,
        jobId: interview?.job_id,
        interviewDate: interview?.interview_date,
        interviewTime: interview?.interview_time,
        durationMinutes: interview?.duration_minutes,
        location: interview?.location,
        meetingLink: interview?.meeting_link,
        interviewType: interview?.interview_type,
        interviewerName: interview?.interviewer_name,
        interviewerEmail: interview?.interviewer_email,
        notes: interview?.notes,
        status: interview?.status,
        createdAt: interview?.created_at,
        updatedAt: interview?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching interviews:', error);
      throw error;
    }
  },

  /**
   * Get all interviews for current employer
   */
  async getEmployerInterviews() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('interview_schedules')?.select(`
          *,
          application:job_applications!interview_schedules_application_id_fkey (
            full_name,
            email,
            phone
          ),
          job:jobs!interview_schedules_job_id_fkey (
            job_title,
            company_name
          )
        `)?.eq('employer_id', user?.id)?.order('interview_date', { ascending: true });

      if (error) throw error;

      return data?.map(interview => ({
        id: interview?.id,
        applicationId: interview?.application_id,
        employerId: interview?.employer_id,
        candidateId: interview?.candidate_id,
        jobId: interview?.job_id,
        interviewDate: interview?.interview_date,
        interviewTime: interview?.interview_time,
        durationMinutes: interview?.duration_minutes,
        location: interview?.location,
        meetingLink: interview?.meeting_link,
        interviewType: interview?.interview_type,
        interviewerName: interview?.interviewer_name,
        interviewerEmail: interview?.interviewer_email,
        notes: interview?.notes,
        status: interview?.status,
        candidateName: interview?.application?.full_name,
        candidateEmail: interview?.application?.email,
        candidatePhone: interview?.application?.phone,
        jobTitle: interview?.job?.job_title,
        companyName: interview?.job?.company_name,
        createdAt: interview?.created_at,
        updatedAt: interview?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching employer interviews:', error);
      throw error;
    }
  },

  /**
   * Update interview status
   */
  async updateInterviewStatus(interviewId, newStatus) {
    try {
      const { data, error } = await supabase?.from('interview_schedules')?.update({ status: newStatus })?.eq('id', interviewId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating interview status:', error);
      throw error;
    }
  },

  /**
   * Send interview notification email to candidate
   */
  async sendInterviewNotification(emailData) {
    try {
      const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
      if (!SUPABASE_URL) {
        throw new Error('VITE_SUPABASE_URL is not configured');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-interview-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData.error || 'Failed to send interview notification');
      }

      const result = await response?.json();
      return result;
    } catch (error) {
      console.error('Error sending interview notification:', error);
      throw error;
    }
  },

  /**
   * Reschedule an interview
   */
  async rescheduleInterview(interviewId, newDate, newTime) {
    try {
      const { data, error } = await supabase?.from('interview_schedules')?.update({
          interview_date: newDate,
          interview_time: newTime,
          status: 'rescheduled'
        })?.eq('id', interviewId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        interviewDate: data?.interview_date,
        interviewTime: data?.interview_time,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error rescheduling interview:', error);
      throw error;
    }
  },

  /**
   * Cancel an interview
   */
  async cancelInterview(interviewId) {
    try {
      const { data, error } = await supabase?.from('interview_schedules')?.update({ status: 'cancelled' })?.eq('id', interviewId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.status,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error cancelling interview:', error);
      throw error;
    }
  }
};